import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { api } from '../../../../singleton/axios';
import { Watchlist } from './useWatchlists';

interface CreateWatchlistData {
  name: string;
}

interface CreateWatchlistResponse {
  data: Watchlist;
}

async function createWatchlist(requestData: CreateWatchlistData): Promise<Watchlist> {
  try {
    const { data } = await api.post<CreateWatchlistResponse>('/stock-watchlists', requestData);
    return data.data;
  } catch (error: any) {
    // Duplicate names and the per-user watchlist limit both come back as a 400
    // with a human-readable message — surface it verbatim.
    throw new Error(
      error.response?.data?.error ??
      `Failed to create watchlist: ${error.response?.status ?? 'Unknown error'}`
    );
  }
}

export function useCreateWatchlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createWatchlist,
    onSuccess: (newWatchlist) => {
      // Refetch rather than splicing into the cache: the list is ordered
      // oldest-first, and a client-side prepend would put the new watchlist
      // where the signup default belongs — silently moving the fallback
      // selection every consumer derives from watchlists[0].
      queryClient.invalidateQueries({ queryKey: ['watchlists'] });
      // A new (empty) list changes the structure the dashboard caches per user.
      queryClient.invalidateQueries({ queryKey: ['dashboard-watchlist'] });
      queryClient.invalidateQueries({ queryKey: ['watchlist-earnings'] });

      // Show success notification
      enqueueSnackbar(`Watchlist "${newWatchlist.name}" created successfully!`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
    },
    onError: (error) => {
      // Show error notification
      enqueueSnackbar(error.message || 'Failed to create watchlist', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    },
  });
}